import React, { useState, useEffect } from 'react';
import {
  getAllLanguages,
  getTopicsByLanguage,
  generateTopicFromReviewWords,
  STATIC_BASE_URL,
} from '../api';

import {
  Box, Typography, Paper, CircularProgress, Alert, Stack,
  FormControl, InputLabel, Select, MenuItem, Button
} from '@mui/material';

const TopicsPage = () => {
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [loading, setLoading] = useState({
    languages: false, topics: false, generate: false
  });
  const [error, setError] = useState(null);
  const [generationUsage, setGenerationUsage] = useState({ used: 0, limit: 5 });
  const isAdmin = localStorage.getItem('userRole') === 'admin';

  useEffect(() => {
    const loadLanguages = async () => {
      try {
        setLoading(prev => ({ ...prev, languages: true }));
        const langs = await getAllLanguages();
        setLanguages(langs);
        if (langs.length > 0) {
          setSelectedLanguage(langs[0].id);
        }
      } catch (err) {
        setError('Ошибка загрузки языков');
        console.error(err);
      } finally {
        setLoading(prev => ({ ...prev, languages: false }));
      }
    };
    loadLanguages();
  }, []);

  useEffect(() => {
    const loadTopics = async () => {
      if (!selectedLanguage) return;
      try {
        setLoading(prev => ({ ...prev, topics: true }));
        const topicsData = await getTopicsByLanguage(selectedLanguage);
        setTopics(topicsData);
        setSelectedTopic(topicsData.length > 0 ? topicsData[0] : null);
        const generatedCount = topicsData.filter(topic => topic.is_generated).length;
        setGenerationUsage(prev => ({ ...prev, used: generatedCount }));
      } catch (err) {
        setError('Ошибка загрузки топиков');
        console.error(err);
      } finally {
        setLoading(prev => ({ ...prev, topics: false }));
      }
    };
    loadTopics();
  }, [selectedLanguage]);

  const handleTopicChange = (event) => {
    const topicId = event.target.value;
    const topic = topics.find(t => t.id === topicId);
    setSelectedTopic(topic);
  };

  const getAudioSources = (audioFile) => {
    if (!audioFile) return [];

    const staticBase = STATIC_BASE_URL.replace(/\/+$/, '');
    const origin = window.location.origin.replace(/\/+$/, '');
    const normalizeFileName = (value) => value.replace(/^\/+static\/+/, '').replace(/^\/+/, '');
    const isPrivateHost = (hostname) => (
      hostname === 'localhost'
      || hostname === '127.0.0.1'
      || /^10\./.test(hostname)
      || /^192\.168\./.test(hostname)
      || /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
    );

    if (/^https?:\/\//i.test(audioFile)) {
      try {
        const parsed = new URL(audioFile);
        if (isPrivateHost(parsed.hostname)) {
          const fileName = normalizeFileName(parsed.pathname);
          return [
            `${staticBase}/${fileName}`,
            `${origin}/api/static/${fileName}`,
            `${origin}/static/${fileName}`,
          ];
        }
      } catch (err) {
        return [audioFile];
      }
      return [audioFile];
    }

    const fileName = normalizeFileName(audioFile);
    return [
      `${staticBase}/${fileName}`,
      `${origin}/api/static/${fileName}`,
      `${origin}/static/${fileName}`,
    ];
  };

  const formatGeneratedTitle = (topic) => {
    if (!topic?.is_generated) return topic?.title || '';
    if (!topic?.created_at) return `${topic.title} (сгенерирован)`;
    const date = new Date(topic.created_at);
    if (Number.isNaN(date.getTime())) return `${topic.title} (сгенерирован)`;
    const formattedDate = date.toLocaleDateString('ru-RU');
    return `${topic.title} (сгенерирован - ${formattedDate})`;
  };

  const handleGenerateTopic = async () => {
    if (!selectedLanguage) return;
    try {
      setError(null);
      setLoading(prev => ({ ...prev, generate: true }));
      const response = await generateTopicFromReviewWords(selectedLanguage);
      const generatedTopic = response.topic;
      setGenerationUsage({
        used: response.generations_used ?? generationUsage.used,
        limit: response.generations_limit ?? generationUsage.limit,
      });
      setTopics(prev => [generatedTopic, ...prev]);
      setSelectedTopic(generatedTopic);
    } catch (err) {
      const message = err?.response?.data?.detail || 'Ошибка генерации текста';
      setError(message);
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, generate: false }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Практика чтения
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 200 }} disabled={loading.languages}>
            <InputLabel>Язык</InputLabel>
            <Select
              value={selectedLanguage || ''}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              label="Язык"
            >
              {languages.map(lang => (
                <MenuItem key={lang.id} value={lang.id}>{lang.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {loading.topics ? (
            <CircularProgress size={24} />
          ) : topics.length > 0 && (
            <FormControl sx={{ minWidth: 200, flex: 1 }} disabled={loading.topics}>
              <InputLabel>Топик</InputLabel>
              <Select
                value={selectedTopic ? selectedTopic.id : ''}
                onChange={handleTopicChange}
                label="Топик"
              >
                {topics.map(topic => (
                  <MenuItem key={topic.id} value={topic.id}>
                    {formatGeneratedTitle(topic)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="contained"
            onClick={handleGenerateTopic}
            disabled={!selectedLanguage || loading.generate || loading.topics}
          >
            {loading.generate ? 'Генерация...' : 'Сгенерировать текст'}
          </Button>
        </Stack>

        <Typography variant="body2" sx={{ mt: 1, opacity: 0.85 }}>
          Текст составляется из слов, где вы недавно ошибались в «Занятии», вашего словаря и изучаемых слов курса.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.85 }}>
          Лимит генераций: {isAdmin ? 'без ограничений для admin' : `${generationUsage.used}/${generationUsage.limit}`}. Генерация текстов использует платный API, проект живет за счет поддержки пользователей. Если нужен больший лимит, напишите мне.
        </Typography>
      </Paper>

      {loading.topics ? (
        <CircularProgress />
      ) : selectedTopic ? (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            {formatGeneratedTitle(selectedTopic)}
          </Typography>

          {/* Аудиоплеер — если файл есть */}
          {selectedTopic.audio_file && (
            <Box sx={{ mt: 2 }}>
              <audio key={selectedTopic.audio_file} controls style={{ width: '100%' }}>
                {getAudioSources(selectedTopic.audio_file).map((src) => (
                  <source key={src} src={src} type="audio/mp4" />
                ))}
                Ваш браузер не поддерживает аудио элемент.
              </audio>
            </Box>
          )}

          {/* Текст топика */}
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 2 }}>
            {selectedTopic.topic}
          </Typography>
        </Paper>
      ) : (
        !loading.languages && <Typography>Для выбранного языка тексты не найдены.</Typography>
      )}
    </Box>
  );
};

export default TopicsPage;
